// API utility for fetching Weather In Port data

import { useCallback } from 'react';

const API_BASE_URL = 'https://xo2a24ey5h.execute-api.eu-west-1.amazonaws.com';

// TypeScript interfaces
export interface PrecipitationData {
  pct_dry: number;
  /** 0.1-0.2 mm/h — sub-bin of pct_light, not disjoint */
  pct_light_drizzle: number;
  /** 0.1-1.0 mm/h — includes pct_light_drizzle (kept for backward compatibility) */
  pct_light: number;
  pct_moderate_low: number;
  pct_moderate: number;
  pct_moderate_high: number;
  pct_heavy: number;
  pct_any_precipitation: number;
}

export interface MonthlyWeatherData extends PrecipitationData {
  avg_rain_duration: number;
  pct_short_events: number;
  pct_medium_events: number;
  pct_long_events: number;
  avg_daily_events: number;
  /** Inter-annual spread of the >=0.2 mm/h rain-time metric (yearly means over the 10-year window) */
  rain_time_min?: number;
  rain_time_max?: number;
  rain_time_std?: number;
  /** Inter-annual spread of the >=0.1 mm/h any-precipitation metric */
  precip_time_min?: number;
  precip_time_max?: number;
  precip_time_std?: number;
}

export interface WeatherInPortResponse {
  port_id: number;
  port_name: string;
  monthly: Record<string, MonthlyWeatherData>;
}

export interface ApiResult<T = unknown> {
  ok: boolean;
  status: number;
  data?: T;
  message?: string;
}

export const useWeatherInPortApi = () => {
  const fetchWeatherInPort = useCallback(
    async (id: string, src?: string | null): Promise<ApiResult<WeatherInPortResponse>> => {
      const params = new URLSearchParams({ type: 'weatherinport', id });
      if (src) params.set('src', src);
      const url = `${API_BASE_URL}/api?${params.toString()}`;

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        if (!response.ok) {
          return {
            ok: false,
            status: response.status,
            message: data.error || 'Failed to fetch weather in port data',
          };
        }

        return {
          ok: true,
          status: response.status,
          data,
        };
      } catch (error) {
        return {
          ok: false,
          status: 0,
          message: error instanceof Error ? error.message : 'Network error',
        };
      }
    },
    []
  );

  return { fetchWeatherInPort };
};

// Month keys for iteration (1-12)
export const MONTH_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'] as const;

// Month display names
export const MONTH_NAMES: Record<string, string> = {
  '1': 'Jan',
  '2': 'Feb',
  '3': 'Mar',
  '4': 'Apr',
  '5': 'May',
  '6': 'Jun',
  '7': 'Jul',
  '8': 'Aug',
  '9': 'Sep',
  '10': 'Oct',
  '11': 'Nov',
  '12': 'Dec',
};

// Intensity category labels
// Note: the "Light" row is displayed as 0.2-1.0 mm/h (pct_light - pct_light_drizzle);
// the raw pct_light field still covers 0.1-1.0 for backward compatibility.
export const INTENSITY_LABELS: Record<string, string> = {
  pct_dry: 'Dry',
  pct_light_drizzle: 'Light Drizzle',
  pct_light: 'Light',
  pct_moderate_low: 'Moderate Low',
  pct_moderate: 'Moderate',
  pct_moderate_high: 'Moderate High',
  pct_heavy: 'Heavy',
};

// Intensity thresholds for display
export const INTENSITY_THRESHOLDS: Record<string, string> = {
  pct_dry: '< 0.1 mm/h',
  pct_light_drizzle: '0.1 - 0.2 mm/h',
  pct_light: '0.2 - 1.0 mm/h',
  pct_moderate_low: '1.0 - 2.0 mm/h',
  pct_moderate: '2.0 - 3.0 mm/h',
  pct_moderate_high: '3.0 - 4.0 mm/h',
  pct_heavy: '>= 4.0 mm/h',
};
